<template>
  <chart-barcode v-bind="chartProps" />
</template>

<script>
import ChartBarcode from '@/components/chart/AppChartBarcode.vue'
import moment from 'moment'
import BarInflateOnHoverPlugin from '@/js/chartjs-plugin/BarInflateOnHoverPlugin.js'
import customTooltipConfig from '@/components/sensor/CustomTooltipConfig.js'
import getTimeTicksRoundOption from '@/components/sensor/TimeTicksRoundOption.js'
import ObjectUtil from '@/js/util/ObjectUtil'
import i18n from '@/i18n.js'
import {
  TEXT_COLOR,
  ENTITY_COLOR_GREEN,
  ENTITY_COLOR_BLUE,
  ENTITY_COLOR_PURPLE,
  ENTITY_COLOR_LIGHT_GREEN,
  ENTITY_COLOR_LIGHT_BLUE,
  ENTITY_COLOR_RED_PURPLE,
  ENTITY_COLOR_BLUE_GREEN,
  ENTITY_COLOR_PINK,
  ENTITY_COLOR_BROWN,
  ENTITY_COLOR_GOLD,
} from '@/assets/scss/variables.module.scss'
import { MAXIMUM_TIME } from '@/constants.js'

// FonrFamily
const FONT_FAMILY = getComputedStyle(document.body, null).getPropertyValue(
  'font-family'
)

// データセットの色定義
const DATASET_COLORS = [
  ENTITY_COLOR_GREEN,
  ENTITY_COLOR_BLUE,
  ENTITY_COLOR_PURPLE,
  ENTITY_COLOR_LIGHT_GREEN,
  ENTITY_COLOR_LIGHT_BLUE,
  ENTITY_COLOR_RED_PURPLE,
  ENTITY_COLOR_BLUE_GREEN,
  ENTITY_COLOR_PINK,
  ENTITY_COLOR_BROWN,
  ENTITY_COLOR_GOLD,
]

// データセットの色定義(2値用)
const DATASET_COLORS_BOOL = ['#94d19f', '#004d3b']

// 透明色
const TRANSPARENT_COLOR = '#ffffff00'

// ユニークキーとする値のキーペア
const UNIQUE_KEY_PAIR = ['sensor_id', 'sensor_item_id']

export default {
  name: 'AppSensorChartBarcode',
  components: {
    'chart-barcode': ChartBarcode,
  },
  props: {
    /**
     * センサーデータ
     */
    data: {
      type: Array,
      default: () => [],
    },
    /**
     * 開始時間
     */
    from: {
      type: [Object, Number],
      default: null,
    },
    /**
     * 終了時間
     */
    to: {
      type: [Object, Number],
      default: null,
    },
    /**
     * コード定義
     */
    valueCodes: {
      type: Array,
      default: () => [],
    },
    /**
     * サンプリング間隔
     */
    rowUnit: {
      type: Number,
      default: 0,
    },
  },
  data: function () {
    return {
      // チャートに渡すprops
      chartProps: {
        data: {},
        options: {},
        plugins: [BarInflateOnHoverPlugin],
      },
    }
  },
  computed: {
    /**
     * コード定義のMap
     * @type {Map}
     */
    valueCodeMap: function () {
      return new Map(this.valueCodes.map((item) => [item.value, item.text]))
    },
    /**
     * コード値から使用色を引き当てる時のindexMap
     * @type {Map}
     */
    valueColorIndexMap: function () {
      return new Map(
        [...this.valueCodes]
          .sort((a, b) => a.value - b.value)
          .map(({ value }, index) => [value, index])
      )
    },
    /**
     * 使用する色定義
     * @type {Array.<string>}
     */
    colorSource: function () {
      return this.valueCodeMap.size === 2 ? DATASET_COLORS_BOOL : DATASET_COLORS
    },
    /**
     * データセット
     * @type {Array.<Object>}
     */
    datasets: function () {
      // 同じ値のデータをまとめる
      const data = this.$_fillBlankData()

      let lastValue = undefined
      for (const d of this.data) {
        if (lastValue === undefined) {
          data.push({
            sensor_id: d.sensor_id,
            sensor_item_id: d.sensor_item_id,
            start: d.sensor_at,
            duration: 0,
            value: d.value,
          })
        } else if (d.value !== lastValue) {
          data[data.length - 1].duration =
            d.sensor_at - data[data.length - 1].start
          data.push({
            sensor_id: d.sensor_id,
            sensor_item_id: d.sensor_item_id,
            start: d.sensor_at,
            duration: 0,
            value: d.value,
          })
        } else {
          data[data.length - 1].duration =
            d.sensor_at - data[data.length - 1].start
        }
        lastValue = d.value
      }

      // まとめたレコード毎に系列を作成する
      return data.map((d) => {
        return {
          value: d.value,
          label: this.valueCodeMap.get(d.value),
          data: [
            {
              sensor_name: this.$_generateLabel(d),
              start: d.start,
              duration: d.duration,
            },
          ],
          backgroundColor: this.$_getColor(d.value),
        }
      })
    },
    /**
     * チャート上の最大時間
     * @type {Moment}
     */
    maxTime: function () {
      const isAboutToMidnight = this.to.format('HH:mm:ss') === MAXIMUM_TIME
      return isAboutToMidnight ? moment(this.to).add(1, 'seconds') : this.to
    },
    /**
     * チャートオプション
     * @type {Object}
     */
    chartOptions: function () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        indexAxis: 'y',
        datasets: {
          bar: {
            grouped: false,
            parsing: {
              xAxisKey: 'duration',
              yAxisKey: 'sensor_name',
            },
            hoverBackgroundColor: ({ dataset }) => dataset.backgroundColor,
          },
        },
        elements: {
          point: {
            backgroundColor: '#ffffff00',
            borderWidth: 0,
            hoverBorderWidth: 2,
          },
        },
        scales: {
          x: ObjectUtil.deepMerge(
            {
              type: 'time',
              stacked: true,
              bounds: 'ticks',
              min: 0,
              max: this.maxTime - this.from,
              title: {
                display: true,
                align: 'end',
                text: i18n.t('appSensorChartBarcode.time'),
                font: {
                  size: 10,
                  family: FONT_FAMILY,
                },
                color: '#b5b5b5',
              },
              time: {
                source: 'data',
                tooltipFormat: 'YYYY/MM/DD HH:mm:ss.SSS',
                round: true,
              },
              ticks: {
                font: {
                  size: 10,
                  family: FONT_FAMILY,
                },
                color: TEXT_COLOR,
                maxRotation: 0,
                callback: (value, index, ticks) => {
                  // ticks は unixtime の
                  const diff = ticks[index].value - ticks[0].value
                  return moment(this.from).add(diff, 'ms').format('HH:mm')
                },
              },
              grid: {
                color: '#e8e8e8',
              },
            },
            getTimeTicksRoundOption(this.from, this.to, +this.from)
          ),
          y: {
            stacked: true,
            title: {
              display: false,
            },
            ticks: {
              display: false,
              callback: () => '', // 表示しない
            },
            grid: {
              tickWidth: 0,
            },
          },
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              filter: (legendItem, chartData) => {
                if (!legendItem.text) return false
                const index = chartData.datasets.findIndex(
                  (d) => d.label === legendItem.text
                )
                return index === legendItem.datasetIndex
              },
              sort: (a, b, chartData) => {
                const aValue = chartData.datasets[a.datasetIndex].value
                const bValue = chartData.datasets[b.datasetIndex].value
                return aValue - bValue
              },
              boxWidth: 14,
              boxHeight: 14,
              padding: 16,
              font: {
                size: 12,
                family: FONT_FAMILY,
              },
              color: TEXT_COLOR,
            },
            onClick: null,
          },
          tooltip: {
            ...customTooltipConfig,
            interaction: {
              intersect: false,
              mode: 'point',
            },
            filter: ({ dataset }) => !!dataset.label,
            callbacks: {
              title: () => '', // タイトル非表示
              label: (context) => {
                const { start, duration, sensor_name } = context.dataset.data[0]
                const durationLabel = `${moment(start).format(
                  'YYYY/MM/DD HH:mm:ss.SSS'
                )} - ${moment(start + duration).format('HH:mm:ss.SSS')}`
                // 3段にするために配列化
                return [
                  durationLabel, // X値(時刻)
                  sensor_name, // Y値
                  context.dataset.label, // 系列名
                ]
              },
            },
          },
          barInflateOnHoverPlugin: {
            inflateAmount: 1,
          },
        },
      }
    },
  },
  watch: {
    data: function () {
      this.$_updateChartProps()
    },
  },
  methods: {
    /**
     * チャートprops更新
     */
    $_updateChartProps: function () {
      this.chartProps = {
        data: { labels: '', datasets: this.datasets },
        options: this.chartOptions,
        plugins: [BarInflateOnHoverPlugin],
      }
    },

    /**
     * 先頭データと表示期間の開始の間を埋める
     */
    $_fillBlankData: function () {
      if (!this.data.length || !this.data[0].start > this.from.unix()) return []

      const { sensor_at, sensor_id, sensor_item_id } = this.data[0]
      const fillData = []
      const firstDate = moment(sensor_at).valueOf()
      let time = this.from.valueOf()
      while (time <= firstDate) {
        fillData.push({
          sensor_id: sensor_id,
          sensor_item_id: sensor_item_id,
          start: time,
          duration: this.rowUnit * 1000,
          value: null,
        })
        time += this.rowUnit * 1000
      }
      return fillData
    },

    /**
     * 描画色を取得する
     * @param {number} value 値
     * @returns {string} カラーコード(hex)
     */
    $_getColor: function (value) {
      return (
        this.colorSource[this.valueColorIndexMap.get(value)] ||
        TRANSPARENT_COLOR
      )
    },

    /**
     * データセットのラベルを生成する
     * @param {Object} datum センサーデータの1レコード
     * @returns {String} データセットラベル
     */
    $_generateLabel: function (datum) {
      return UNIQUE_KEY_PAIR.map((key) => ({ key, value: datum[key] }))
        .map((item) => {
          switch (item.key) {
            case 'sensor_id':
              return this.$store.getters['sensor/getSensorById'](item.value)
                .sensor_name
            case 'sensor_item_id':
              return this.$store.getters['sensorItem/getSensorItemById'](
                item.value
              ).sensor_item_name
            default:
              return ''
          }
        })
        .join(':')
    },
  },
}
</script>
